import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Parameter } from 'shapediver-types';
import { ChromePicker, ColorResult, RGBColor } from 'react-color';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import { debounce } from 'lodash';

type ParameterDefinition = Parameter.ParameterDefinition;

function toShapeDiver(color: RGBColor): string {
  const { r, g, b, a = 1 } = color;
  const alphaTo255 = Math.round(a * 255);

  function numHex(s: number): string {
    let a = s.toString(16);
    if (a.length % 2 > 0) {
      a = '0' + a;
    }
    return a;
  }

  return `0x${numHex(r)}${numHex(g)}${numHex(b)}${numHex(alphaTo255)}`;
}

function toReactColor(val: string): RGBColor {
  function parse(from: number, to: number): number {
    return parseInt(val.slice(from, to), 16);
  }

  const r = parse(2, 4);
  const g = parse(4, 6);
  const b = parse(6, 8);
  const a = parse(8, 10) / 255;

  return { r, g, b, a };
}

type Props = {
  //0xRRGGBBAA
  initialValue: string;
  definition: ParameterDefinition;
  onChange: (newValue: string) => void;
};

const ColorInput = (props: Props): React.ReactElement => {
  const { definition, initialValue, onChange } = props;
  const { id } = definition;

  const [value, setValue] = useState<RGBColor>(toReactColor(initialValue));

  const onChangeDebounced = useMemo(() => {
    return debounce(onChange, 100);
  }, [onChange]);

  const onColorChange = useCallback(
    (color: ColorResult): void => {
      // console.log('------------------', color.rgb, toShapeDiver(color.rgb));
      setValue(color.rgb);
      onChangeDebounced(toShapeDiver(color.rgb));
    },
    [onChangeDebounced]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const ariaId = open ? id : undefined;

  return (
    <div style={{ marginTop: 5 }}>
      <Button
        variant="outlined"
        color="primary"
        aria-describedby={ariaId}
        onClick={onButtonClick}
      >
        <span
          style={{
            display: 'inline-block',
            width: 50,
            height: 30,
            backgroundColor: `rgb(${value.r}, ${value.g}, ${value.b}, ${value.a}`,
          }}
        />
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <ChromePicker color={value} onChange={onColorChange} />
      </Popover>
    </div>
  );
};

export default ColorInput;
